import React, { useState, useEffect } from 'react'
// Removed react-helmet-async for React 19 compatibility
// import { Helmet, HelmetProvider } from "react-helmet-async"
import { lightTheme, darkTheme } from './styles/theme';
import { ThemeProvider } from 'styled-components';
import { GlobalStyle } from './styles/globalStyles';
import Layout from './components/Layout/Layout';
import Routes from './Routes';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NavigationProvider } from './context/NavigationContext';
import { RegionProvider } from './context/RegionContext';
import { ConsentProvider } from './context/ConsentContext';
import { ConsentBanner, ConsentPreferenceModal } from './components/Consent';
import { WebSocketProvider } from './contexts/WebSocketProvider';
import { PresenceProvider } from './contexts/PresenceProvider';
import { NotificationProvider } from './context/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import ToastProvider from './components/common/ToastProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FAVICONS, APP_TITLE } from './constants/staticAssets';

export const ThemeContext = React.createContext(null);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 2, // 2 minutes before data is considered stale
            gcTime: 1000 * 60 * 10, // 10 minutes cache before garbage collection
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

// WebSocket Wrapper Component to provide auth context to WebSocketProvider
const WebSocketWrapper = ({ children }) => {
    const { currentUser } = useAuth();
    
    return (
        <WebSocketProvider 
            user={currentUser}
            enabled={!!currentUser}
        >
            <PresenceProvider>
                <NotificationProvider>
                    {children}
                </NotificationProvider>
            </PresenceProvider>
        </WebSocketProvider>
    );
};

const App = () => {
    const [currentTheme, setTheme] = useState("light");
    const theme = currentTheme === 'light' ? lightTheme : darkTheme;

    // Set document title and meta tags using native DOM API
    useEffect(() => {
        document.title = APP_TITLE;
        
        // Use the app's current theme state
        const themeIcons = currentTheme === 'dark' ? FAVICONS.dark : FAVICONS.light;
        
        // Set favicon (32px for standard browser tabs)
        let favicon = document.querySelector("link[rel='icon']:not([sizes])") || document.createElement('link');
        favicon.type = 'image/png';
        favicon.rel = 'icon';
        favicon.href = themeIcons['32'];
        document.getElementsByTagName('head')[0].appendChild(favicon);
        
        // Set 16x16 favicon for smaller displays
        let favicon16 = document.querySelector("link[rel='icon'][sizes='16x16']") || document.createElement('link');
        favicon16.type = 'image/png';
        favicon16.rel = 'icon';
        favicon16.sizes = '16x16';
        favicon16.href = themeIcons['16'];
        document.getElementsByTagName('head')[0].appendChild(favicon16);

        // Set apple touch icon (48px - largest available)
        let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") || document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        appleTouchIcon.href = themeIcons['48'];
        document.getElementsByTagName('head')[0].appendChild(appleTouchIcon);

        // Add Google Fonts preconnect and stylesheet
        const fontPreconnects = [
            { href: 'https://fonts.googleapis.com', crossOrigin: false },
            { href: 'https://fonts.gstatic.com', crossOrigin: true }
        ];

        fontPreconnects.forEach(({ href, crossOrigin }) => {
            if (!document.querySelector(`link[href="${href}"]`)) {
                let link = document.createElement('link');
                link.rel = 'preconnect';
                link.href = href;
                if (crossOrigin) link.crossOrigin = 'true';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
        });

        // Add Google Fonts stylesheet
        const fontStylesheet = 'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&family=Nunito:ital,wght@0,200..1000;1,200..1000&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap';
        if (!document.querySelector(`link[href="${fontStylesheet}"]`)) {
            let link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = fontStylesheet;
            document.getElementsByTagName('head')[0].appendChild(link);
        }
    }, [currentTheme]); // Re-run when theme changes

    return (
        <ThemeContext.Provider value={{ setTheme, theme: currentTheme }}>
            <ThemeProvider theme={theme}>
                <GlobalStyle />
                <ErrorBoundary>
                    <QueryClientProvider client={queryClient}>
                    <ToastProvider>
                        <NavigationProvider>
                            <RegionProvider>
                                <ConsentProvider>
                                    <AuthProvider>
                                        <WebSocketWrapper>
                                            <Layout>
                                                <Routes />
                                            </Layout>
                                            <ConsentBanner />
                                            <ConsentPreferenceModal />
                                        </WebSocketWrapper>
                                    </AuthProvider>
                                </ConsentProvider>
                            </RegionProvider>
                        </NavigationProvider>
                    </ToastProvider>
                    </QueryClientProvider>
                </ErrorBoundary>
            </ThemeProvider>
        </ThemeContext.Provider>
    );
}

export default App;
